import type {
  CareOffer,
  CareRef,
  CareType,
  CareRequestBody,
  CareRequestResult,
  CarePlace,
  CollectionData,
  FinderData,
  FormAnswers,
  HouseholdMember,
  LocationGroup,
  MyCareRequest,
  MyServeResponse,
  ResponseResult,
  ServeDetailData,
  ServeForm,
} from './types';
import { getMpWidgetToken } from './campus';
import { mpFetch } from '@church/nextjs-ui/website/mp-session';

// Vite bakes these at build time via `define` in vite.config.ts.
const BAKED_API_KEY = process.env.CHURCH_HUB_API_KEY ?? '';
const BAKED_APP_SLUG = process.env.CHURCH_HUB_APP_SLUG ?? '';

function headers(): Record<string, string> {
  const h: Record<string, string> = {};
  if (BAKED_API_KEY) h['x-api-key'] = BAKED_API_KEY;
  if (BAKED_APP_SLUG) h['x-app-slug'] = BAKED_APP_SLUG;
  return h;
}

function base(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/$/, '');
}

const RETRY_DELAYS_MS = [1500, 3000];

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });
}

/**
 * GET with two retries on a 5xx or network error: this runs on a public page,
 * and a brief edge hiccup should not show visitors an error card.
 */
async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1], signal);
    try {
      const res = await fetch(url, { headers: headers(), signal });
      if (res.ok) return (await res.json()) as T;
      lastError = new Error(`${url} responded ${res.status}`);
      if (res.status < 500) break; // a 4xx will not fix itself
    } catch (err) {
      if (signal?.aborted) throw err;
      lastError = err;
    }
  }
  throw lastError;
}

const is404 = (err: unknown) => err instanceof Error && /responded 404/.test(err.message);

/** Every public serve opportunity plus filter options. */
export function fetchFinderData(apiBaseUrl: string, signal?: AbortSignal): Promise<FinderData> {
  return getJson<FinderData>(`${base(apiBaseUrl)}/api/serve`, signal);
}

/** One opportunity for the details page; null when the catalogue does not list it. */
export async function fetchOpportunity(
  apiBaseUrl: string,
  id: number,
  signal?: AbortSignal
): Promise<ServeDetailData | null> {
  try {
    return await getJson<ServeDetailData>(`${base(apiBaseUrl)}/api/serve/${id}`, signal);
  } catch (err) {
    if (is404(err)) return null;
    throw err;
  }
}

/** The opportunity's custom form; null when it has none. */
export async function fetchForm(
  apiBaseUrl: string,
  id: number,
  signal?: AbortSignal
): Promise<ServeForm | null> {
  try {
    const data = await getJson<{ form: ServeForm }>(
      `${base(apiBaseUrl)}/api/serve/${id}/form`,
      signal
    );
    return data.form;
  } catch (err) {
    if (is404(err)) return null;
    throw err;
  }
}

/** Campuses grouped by location, with each location's brand SVG. */
export async function fetchLocations(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<LocationGroup[]> {
  const data = await getJson<unknown>(`${base(apiBaseUrl)}/api/locations`, signal);
  // An error body ({ error }) must not take the finder down with it.
  return Array.isArray(data) ? (data as LocationGroup[]) : [];
}

async function getWithToken<T>(url: string, signal?: AbortSignal): Promise<T | null> {
  const token = getMpWidgetToken();
  if (!token) return null;
  try {
    const res = await mpFetch(url, {
      headers: { ...headers(), Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch (err) {
    if (signal?.aborted) throw err;
    return null;
  }
}

/**
 * The signed-in visitor's default campus (Contacts.Web_Congregation_ID,
 * falling back to their household's campus). Null when not signed in.
 */
export async function fetchHomeCampus(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<number | null> {
  const data = await getWithToken<{ congregationId: number | null }>(
    `${base(apiBaseUrl)}/api/user/home-campus`,
    signal
  );
  return data?.congregationId ?? null;
}

/** Persist a campus choice to MP for the signed-in visitor. No-op when signed out. */
export async function setHomeCampus(apiBaseUrl: string, congregationId: number): Promise<boolean> {
  const token = getMpWidgetToken();
  if (!token) return false;
  try {
    const res = await mpFetch(`${base(apiBaseUrl)}/api/set-home-campus`, {
      method: 'POST',
      headers: {
        ...headers(),
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ congregationId }),
    });
    return res.ok;
  } catch (err) {
    console.error('Serve finder: could not save home campus', err);
    return false;
  }
}

/** The household's recent serve responses. Null when signed out or on error. */
export async function fetchMyResponses(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<MyServeResponse[] | null> {
  const data = await getWithToken<{ responses: MyServeResponse[] }>(
    `${base(apiBaseUrl)}/api/serve/me`,
    signal
  );
  return data?.responses ?? null;
}

/** The household's care requests (one per household per Collection). Null when signed out or on error. */
export async function fetchMyCare(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<MyCareRequest[] | null> {
  const data = await getWithToken<{ requests: MyCareRequest[] }>(
    `${base(apiBaseUrl)}/api/care/me`,
    signal
  );
  return data?.requests ?? null;
}

/** The signed-in person and the others in their household. Null when signed out. */
export async function fetchHousehold(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<HouseholdMember[] | null> {
  const data = await getWithToken<{ members: HouseholdMember[] }>(
    `${base(apiBaseUrl)}/api/user/household`,
    signal
  );
  return data?.members ?? null;
}

export class ResponseError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly url?: string
  ) {
    super(message);
  }
}

/** Raise a hand for an opportunity. Requires the MP widget token. */
export async function submitResponse(
  apiBaseUrl: string,
  opportunityId: number,
  input: {
    people: { contactId: number }[];
    comments: string;
    answers?: FormAnswers;
    website?: string;
  }
): Promise<ResponseResult> {
  const token = getMpWidgetToken();
  if (!token) throw new ResponseError('Sign in to respond', 401);
  const res = await mpFetch(`${base(apiBaseUrl)}/api/serve/${opportunityId}/responses`, {
    method: 'POST',
    headers: { ...headers(), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    let message = 'Could not send your response';
    let url: string | undefined;
    try {
      const data = (await res.json()) as { error?: string; url?: string };
      if (data.error) message = data.error;
      url = data.url;
    } catch {
      /* no body */
    }
    throw new ResponseError(message, res.status, url);
  }
  return (await res.json()) as ResponseResult;
}

/** Who in the signed-in household holds a current CPP (GET /api/serve/cpp). */
export async function fetchCppCleared(apiBaseUrl: string): Promise<Set<number>> {
  const token = getMpWidgetToken();
  if (!token) return new Set();
  const res = await mpFetch(`${base(apiBaseUrl)}/api/serve/cpp`, {
    headers: { ...headers(), Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`cpp ${res.status}`);
  const data = (await res.json()) as { cleared?: number[] };
  return new Set(data.cleared ?? []);
}

/** "Let Caleb know": one email naming every CPP-locked role they looked at. */
export async function askAboutCpp(
  apiBaseUrl: string,
  input: { contactId: number; opportunityIds: number[]; note?: string }
): Promise<void> {
  const token = getMpWidgetToken();
  if (!token) throw new ResponseError('Sign in first', 401);
  const res = await mpFetch(`${base(apiBaseUrl)}/api/serve/cpp-interest`, {
    method: 'POST',
    headers: { ...headers(), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    let message = 'Could not send it';
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      /* no body */
    }
    throw new ResponseError(message, res.status);
  }
}

/** Drop serve shifts (responses) the household holds; `added` names this save's new sign-ups for the email. */
export async function cancelShifts(
  apiBaseUrl: string,
  input: { responseIds: number[]; added?: number[] }
): Promise<void> {
  const token = getMpWidgetToken();
  if (!token) throw new ResponseError('Sign in first', 401);
  const res = await mpFetch(`${base(apiBaseUrl)}/api/serve/cancel`, {
    method: 'POST',
    headers: { ...headers(), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    let message = 'Could not change that right now';
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      /* no body */
    }
    throw new ResponseError(message, res.status);
  }
}

/**
 * After a save in a collection: one email per person with everything they now
 * hold in it. Best effort; the save already happened.
 */
export async function confirmServe(
  apiBaseUrl: string,
  input: {
    collectionId: number;
    people: { contactId: number; added: number[]; dropped: number[] }[];
  }
): Promise<void> {
  const token = getMpWidgetToken();
  if (!token) return;
  await mpFetch(`${base(apiBaseUrl)}/api/serve/confirm`, {
    method: 'POST',
    headers: { ...headers(), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    keepalive: true,
  }).catch(() => undefined);
}

/* ---------- Collections and care ---------- */

/** A Collection by id or slug (a path like thanksgiving-boxes); null when there is none. */
export async function fetchCollection(
  apiBaseUrl: string,
  ref: string | number,
  signal?: AbortSignal
): Promise<CollectionData | null> {
  try {
    return await getJson<CollectionData>(
      `${base(apiBaseUrl)}/api/serve/collection?ref=${encodeURIComponent(String(ref))}`,
      signal
    );
  } catch (err) {
    if (is404(err)) return null;
    throw err;
  }
}

/** What a /care path names: a programme, or one offer. Null when nothing. */
export async function fetchCare(
  apiBaseUrl: string,
  ref: string,
  signal?: AbortSignal
): Promise<CareRef | null> {
  try {
    const path = ref.split('/').map(encodeURIComponent).join('/');
    return await getJson<CareRef>(`${base(apiBaseUrl)}/api/care/${path}`, signal);
  } catch (err) {
    if (is404(err)) return null;
    throw err;
  }
}

/** The middle of a zip code, or null when the church's addresses cannot say. */
export async function fetchZipPoint(
  apiBaseUrl: string,
  zip: string,
  signal?: AbortSignal
): Promise<{ lat: number; lng: number } | null> {
  try {
    const p = await getJson<{ lat: number | null; lng: number | null }>(
      `${base(apiBaseUrl)}/api/care/zip/${encodeURIComponent(zip)}`,
      signal
    );
    return p.lat != null && p.lng != null ? { lat: p.lat, lng: p.lng } : null;
  } catch {
    return null;
  }
}

/**
 * Places a care search could mean: a city or zip typed, or the device's
 * position (nearest zip). Empty on no match or error.
 */
export async function searchPlaces(
  apiBaseUrl: string,
  by: { q: string } | { lat: number; lng: number },
  signal?: AbortSignal
): Promise<CarePlace[]> {
  const qs =
    'q' in by
      ? `q=${encodeURIComponent(by.q)}`
      : `lat=${by.lat.toFixed(3)}&lng=${by.lng.toFixed(3)}`;
  try {
    const data = await getJson<{ places: CarePlace[] }>(
      `${base(apiBaseUrl)}/api/care/places?${qs}`,
      signal
    );
    return data.places ?? [];
  } catch (err) {
    if (signal?.aborted) throw err;
    // A zip still works through the older lookup.
    if ('q' in by && /^\d{5}$/.test(by.q)) {
      const p = await fetchZipPoint(apiBaseUrl, by.q, signal);
      if (p) return [{ label: by.q, zip: by.q, zips: [by.q], ...p }];
    }
    return [];
  }
}

/** Why a care request was refused, with what the page needs to say so. */
export class CareRequestError extends Error {
  constructor(
    message: string,
    public status: number,
    public state?: string,
    public externalUrl?: string,
    public existing?: { title: string; when: string | null }
  ) {
    super(message);
  }
}

/** A family asking for care. No sign-in: the API finds or makes the household. */
/** A care offer's questions; null when it has none. */
export async function fetchCareForm(
  apiBaseUrl: string,
  offerId: number,
  signal?: AbortSignal
): Promise<ServeForm | null> {
  try {
    const data = await getJson<{ form: ServeForm }>(
      `${base(apiBaseUrl)}/api/care/offers/${offerId}/form`,
      signal
    );
    return data.form;
  } catch (err) {
    if (is404(err)) return null;
    throw err;
  }
}

export async function submitCareRequest(
  apiBaseUrl: string,
  offerId: number,
  body: CareRequestBody
): Promise<CareRequestResult> {
  const token = getMpWidgetToken();
  const res = await mpFetch(`${base(apiBaseUrl)}/api/care/offers/${offerId}/requests`, {
    method: 'POST',
    headers: {
      ...headers(),
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new CareRequestError(
      typeof data.error === 'string' ? data.error : 'We could not save that right now.',
      res.status,
      typeof data.state === 'string' ? data.state : undefined,
      typeof data.externalUrl === 'string' ? data.externalUrl : undefined,
      (data.existing as { title: string; when: string | null } | undefined) ?? undefined
    );
  }
  return data as unknown as CareRequestResult;
}

/** Every public care programme and offer, for /care itself. */
export function fetchCareCatalogue(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<{ types: CareType[]; offers: CareOffer[] }> {
  return getJson<{ types: CareType[]; offers: CareOffer[] }>(
    `${base(apiBaseUrl)}/api/care`,
    signal
  );
}
